import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { materialService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Material as MaterialType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import useSetting from "/app/src/components/settings/setting";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import { useAuthState } from "/app/src/contexts/authentication";
/**
 * Displays a list of materials with dynamic fields and pagination.
 *
 * @returns {JSX.Element} The rendered Materials component.
 */
export default function Materials() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const goToMaterial = useCallback(
    (materialId: number) => {
      navigate(`/explorer/materials/${materialId}`);
    },
    [navigate],
  );

  const {
    data: materials,
    count: materialsCount,
    isFetching,
  } = usePaginatedData<MaterialType>({
    queryKey: ["materials"],
    searchString,
    sort,
    page,
    pageSize,
    service: materialService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: materialsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<MaterialType>();

  const availableColumns = [
    { id: "Info1", label: t("translation:info1") },
    { id: "codes", label: t("translation:codes") },
    { id: "currentQuantity", label: t("translation:current_quantity") },
    { id: "netQuantity", label: t("translation:net_quantity") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "material", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "material",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            cell: (info) => info.getValue(),
            header: () => t("translation:name"),
          });
        case "Info1":
          return columnHelper.accessor("Info1", {
            id: "Info1",
            cell: (info) => info.getValue(),
            header: () => t("translation:info1"),
          });
        case "codes":
          return columnHelper.accessor("codes", {
            id: "codes",
            cell: (info) => info.getValue(),
            header: () => t("translation:codes"),
          });
        case "currentQuantity":
          return columnHelper.accessor("currentQuantity", {
            id: "currentQuantity",
            cell: (info) => info.getValue(),
            header: () => t("translation:current_quantity"),
          });
        case "netQuantity":
          return columnHelper.accessor("netQuantity", {
            id: "netQuantity",
            cell: (info) => info.getValue(),
            header: () => t("translation:net_quantity"),
          });
        default:
          return null;
      }
    };
    return buildExplorerColumns(settingValue, "material", makeColumn, "name");
  }, [columnHelper, settingValue, t]);

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:materials")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:materials") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={materials}
        tableColumns={columns}
        length={materialsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToMaterial}
        emptyText={t("translation:no_materials")}
      />
    </div>
  );
}
